// Офлайн-снапшот карты доступности модулей (MODULE-PLATFORM-ARCHITECTURE §6.4).
//
// ┌─ Почему localStorage, а не `idb-keyval` и не персист TanStack Query ───────┐
// │ Требование одно и жёсткое: карта читается СИНХРОННО, до первого рендера    │
// │ навигации. Иначе меню сначала рисуется полным, а через кадр из него        │
// │ исчезают пункты — то самое мигание, ради устранения которого снапшот и     │
// │ заводится.                                                                 │
// │                                                                            │
// │ • `idb-keyval` (как в очередях аплоада) — IndexedDB, доступ ТОЛЬКО         │
// │   асинхронный. Синхронно прочитать нельзя ни в каком браузере; текст §6.4  │
// │   писался до того, как это ограничение упёрлось в «до первого рендера».    │
// │   Держать снапшот там — значит либо мигать меню, либо блокировать первый   │
// │   кадр на промисе.                                                         │
// │ • Whitelist персиста TanStack Query (`lib/query-client.ts`) — хранилище    │
// │   синхронное, но восстановление кэша идёт промисом `persistQueryClient`,   │
// │   а срок жизни записи задаётся общим `maxAge: 24h` и сборкой мусора        │
// │   кэша. Снапшот обязан переживать сутки офлайна (командировка, метро),     │
// │   поэтому его срок жизни не должен зависеть от политики кэша запросов.     │
// │                                                                            │
// │ Отсюда — свой ключ в localStorage: синхронное чтение, свой срок жизни,     │
// │ своя версия формата. Объём — два десятка коротких записей, доли килобайта. │
// └────────────────────────────────────────────────────────────────────────────┘

import {
	type ModuleAvailabilityOutput,
	moduleAvailabilityOutputSchema,
} from "@studyhub/shared/contracts/modules";
import { MODULE_PLATFORMS } from "@studyhub/shared/module";
import { z } from "zod";
import { readLocalItem, removeLocalItem, writeLocalItem } from "./local-store";
import { CLIENT_MODULE_PLATFORM } from "./platform";

const SNAPSHOT_KEY = "studyhub:module-availability";

/**
 * Версия ФОРМАТА хранения — не путать с `version` внутри ответа сервера
 * (то поколение карты, оно растёт при каждом изменении раскатки).
 *
 * Снапшот другой версии формата клиент выбрасывает целиком, а не пытается
 * домигрировать: карта дёшево пересчитывается одним запросом, а половинчатая
 * миграция состояния доступности — это неверно показанный или неверно скрытый
 * раздел, то есть худший из возможных исходов.
 */
const SNAPSHOT_FORMAT_VERSION = 2;

/**
 * Предельный возраст снапшота — 30 суток.
 *
 * Верхняя граница нужна не ради свежести (её обеспечивают сокет-событие и
 * рефетч), а против «воскресшего» устройства: телефон, пролежавший полгода в
 * ящике, не должен открыть первый экран по карте доступности полугодовой
 * давности. За пределом снапшот считается отсутствующим — клиент уходит в
 * режим «неизвестно» (читать можно, генерировать нельзя), что честнее.
 */
const MAX_SNAPSHOT_AGE_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Конверт снапшота.
 *
 * Ответ сервера лежит внутри как есть (`payload`), а конверт добавляет ровно
 * то, чего в ответе нет и быть не должно: версию формата хранения, платформу
 * сборки и время записи. Платформа — потому что `platform_unsupported`
 * считается именно по ней, и снапшот мобильного браузера не описывает сборку
 * Capacitor. Принадлежность ПОЛЬЗОВАТЕЛЮ проверять здесь нечем и незачем: она
 * уже есть в самом ответе (`payload.userId`) и сверяется при чтении карты.
 */
const snapshotEnvelopeSchema = z.object({
	formatVersion: z.literal(SNAPSHOT_FORMAT_VERSION),
	platform: z.enum(MODULE_PLATFORMS),
	savedAt: z.string().datetime({ offset: true }),
	payload: moduleAvailabilityOutputSchema,
});
export type ModuleAvailabilitySnapshot = z.infer<typeof snapshotEnvelopeSchema>;

/**
 * Прочитать снапшот. Синхронно и без исключений — вызывается из инициализатора
 * стора, то есть на пути первого рендера.
 *
 * Возвращает `null`, если снапшота нет, он повреждён, написан другой версией
 * формата, снят на другой платформе или устарел. Валидация обязательна:
 * содержимое localStorage — недоверенный вход (переживает откат релиза,
 * правится расширением браузера, остаётся от прошлой версии схемы).
 */
export function readModuleAvailabilitySnapshot(): ModuleAvailabilitySnapshot | null {
	const raw = readLocalItem(SNAPSHOT_KEY);
	if (!raw) return null;

	let parsedJson: unknown;
	try {
		parsedJson = JSON.parse(raw);
	} catch {
		clearModuleAvailabilitySnapshot();
		return null;
	}

	const parsed = snapshotEnvelopeSchema.safeParse(parsedJson);
	if (!parsed.success) {
		// Мусор или прошлая версия формата — снимаем, чтобы не разбирать его
		// повторно на каждом старте.
		clearModuleAvailabilitySnapshot();
		return null;
	}

	if (parsed.data.platform !== CLIENT_MODULE_PLATFORM) {
		clearModuleAvailabilitySnapshot();
		return null;
	}

	const ageMs = Date.now() - Date.parse(parsed.data.savedAt);
	if (!Number.isFinite(ageMs) || ageMs > MAX_SNAPSHOT_AGE_MS) {
		clearModuleAvailabilitySnapshot();
		return null;
	}

	return parsed.data;
}

/** Записать снапшот. Ошибку записи (квота, приватный режим) глотаем молча. */
export function writeModuleAvailabilitySnapshot(payload: ModuleAvailabilityOutput): void {
	const envelope: ModuleAvailabilitySnapshot = {
		formatVersion: SNAPSHOT_FORMAT_VERSION,
		platform: CLIENT_MODULE_PLATFORM,
		savedAt: new Date().toISOString(),
		payload,
	};
	writeLocalItem(SNAPSHOT_KEY, JSON.stringify(envelope));
}

export function clearModuleAvailabilitySnapshot(): void {
	removeLocalItem(SNAPSHOT_KEY);
}
